/**
 * A counter that ticks on every doc change, coalesced to one per frame —
 * for surfaces that re-derive something cheap from the whole doc (the BGM
 * slot layer, the Shorts rail) rather than observing one map. Extracted
 * from BgmSlotLayer (F-126) when the rail needed the same trigger (F-141).
 */
import { useEffect, useRef, useState } from "react";

import type { BoardSession } from "../../core/session.js";

export function useDocVersion(session: BoardSession): number {
  const [version, setVersion] = useState(0);
  const frame = useRef(0);
  useEffect(() => {
    const bump = () => {
      // Coalesce a burst (an import, a paste, a translation batch) into one
      // re-render per frame.
      if (frame.current !== 0) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        setVersion((v) => v + 1);
      });
    };
    const doc = session.board.doc;
    doc.on("update", bump);
    return () => {
      doc.off("update", bump);
      if (frame.current !== 0) cancelAnimationFrame(frame.current);
    };
  }, [session]);
  return version;
}
